// src/components/dashboard/ManageUserVMs.js
import React, { useState, useEffect } from "react";
import { Row, Col, Form, Input, Button, Card, CardBody, Spinner, Alert } from "reactstrap";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ComponentCard from "./ComponentCard";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  startUserVmAPI,
  listUserVmsAPI,
  startUserVmInstanceAPI,
  stopUserVmInstanceAPI,
  updateAssignedUsersAPI,
  deleteUserVmAPI, 
} from "../../service/ManageUserVMsService";
import { getOrganizationsAPI, getUsersByOrgAPI } from "../../service/ManageUserCreditsService";
import "../../App.css";

const authService = require("../../service/AuthService");

const trim = (s) => (s ?? "").trim();

export default function ManageUserVMs() {
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);

  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersCache, setUsersCache] = useState({});
  const sessionUser = authService.getUser?.() || {};
  const username = (
    sessionUser.user_name ||
    sessionUser.username ||
    sessionUser.name ||
    ""
  )
    .toLowerCase()
    .trim();

  // Create VM form
  const [targetOrg, setTargetOrg] = useState("");
  const [targetUser, setTargetUser] = useState(""); 
  const [instanceName, setInstanceName] = useState("");
  const [acceptCosts, setAcceptCosts] = useState(false);
  const [acceptCostsErr, setAcceptCostsErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });

  // VM list / status
  const [vmRows, setVmRows] = useState([]);
  const [statusError, setStatusError] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);

  const [allUsers, setAllUsers] = useState([]);      // [{ username, assigned_vm }]
  const [vmMetaById, setVmMetaById] = useState({});  // { [instanceId]: { instance_name, region, launch_time } }
  const [statesById, setStatesById] = useState({});  // { [instanceId]: "running" | "stopped" | ... }

  // Edit assigned users (checkbox-based)
  const [editingInstanceId, setEditingInstanceId] = useState("");
  const [editAssignedUsersArr, setEditAssignedUsersArr] = useState([]);
  const [unassignedUsers, setUnassignedUsers] = useState([]);
  const [moveWarningsByUser, setMoveWarningsByUser] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingInstanceId, setDeletingInstanceId] = useState("");
  const [actionById, setActionById] = useState({});

  const validateAcceptCosts = (val) => {
    if (!val) return "You must acknowledge that this will consume credits / incur cloud costs.";
    return "";
  };

  const formValid =
    trim(instanceName) &&
    acceptCosts &&
    (!trim(targetOrg) || (trim(targetOrg) && trim(targetUser)));

  // -------------------- load orgs --------------------
  useEffect(() => {
    (async () => {
      try {
        setOrgLoading(true);
        const data = await getOrganizationsAPI({ limit: 500 });
        const items = Array.isArray(data?.items) ? data.items : [];
        setOrgOptions(items);
      } catch (e) {
        setStatus({ type: "danger", msg: e?.message || "Failed to load organizations." });
      } finally {
        setOrgLoading(false);
      }
    })();
  }, []);

  // -------------------- load users per org --------------------
  useEffect(() => {
    const org = trim(targetOrg).toLowerCase();
    if (!org) {
      setUserOptions([]);
      setTargetUser("");
      return;
    }

    const norm = (x) => String(x || "").trim().toLowerCase();

    if (usersCache[org]) {
      const cached = usersCache[org];
      setUserOptions(cached);

      if (targetUser) {
        const cachedSet = new Set(cached.map(norm));
        if (!cachedSet.has(norm(targetUser))) setTargetUser("");
      }
      return;
    }


    (async () => {
      try {
        setUsersLoading(true);
        const data = await getUsersByOrgAPI(org, { limit: 2000 });
        const items = Array.isArray(data?.items) ? data.items : [];
        setUserOptions(items);
        setUsersCache((prev) => ({ ...prev, [org]: items }));
        if (targetUser && !items.includes(targetUser)) setTargetUser("");
      } catch (e) {
        setUserOptions([]);
        setTargetUser("");
        setStatus({ type: "danger", msg: e?.message || "Failed to load users for org." });
      } finally {
        setUsersLoading(false);
      }
    })();
  }, [targetOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------- helpers: assigned users parsing --------------------
  const parseAssignedUsers = (s) =>
    (s || "")
      .split(",")
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);

  const normalizeAssignedUsers = (arrOrString) => {
    if (Array.isArray(arrOrString)) {
      return arrOrString.map((x) => String(x).trim().toLowerCase()).filter(Boolean).join(",");
    }
    return (arrOrString || "")
      .split(",")
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean)
      .join(",");
  };
  

  const isChecked = (u) => (editAssignedUsersArr || []).includes((u || "").toLowerCase());

  const toggleUser = (u) => {
    const user = (u || "").toLowerCase();
    setEditAssignedUsersArr((prev) => {
      const set = new Set((prev || []).map((x) => String(x).toLowerCase()));
      if (set.has(user)) set.delete(user);
      else set.add(user);
      return Array.from(set);
    });
  };

  const assignedVmByUser = React.useMemo(() => {
    const m = {};
    for (const row of allUsers || []) {
      const name = (row?.username || "").trim();
      const iid = (row?.assigned_vm || "").trim();
      if (name) m[name.toLowerCase()] = iid || "";
    }
    return m;
  }, [allUsers]);

  const getVmLabel = (instanceId) => {
    const meta = vmMetaById?.[instanceId];
    return meta?.instance_name ? meta.instance_name : instanceId;
  };

  const onToggleUserWithWarning = (usernameRaw, currentAssignedVmId) => {
    const u = String(usernameRaw || "").trim().toLowerCase();
    const currently = String(currentAssignedVmId || "").trim();

    const isCurrentlyOnDifferentVm =
      currently && currently !== editingInstanceId;

    const alreadyChecked = isChecked(u);

    // If user is being ADDED (not removed) and is assigned elsewhere → warn/confirm
    if (!alreadyChecked && isCurrentlyOnDifferentVm) {
      const fromLabel = getVmLabel(currently);
      const ok = window.confirm(
        `${u} is currently assigned to "${fromLabel}".\n\n` +
        `If you continue, ${u} will be moved to this VM (and removed from the other VM).`
      );

      if (!ok) return;

      // Remember a warning badge in UI (optional but useful)
      setMoveWarningsByUser((prev) => ({
        ...prev,
        [u]: { from: currently, fromLabel },
      }));
    }

    // If user is being removed, clear warning
    if (alreadyChecked) {
      setMoveWarningsByUser((prev) => {
        const copy = { ...prev };
        delete copy[u];
        return copy;
      });
    }

    toggleUser(u);
  };


  // -------------------- edit actions --------------------
  const startEdit = (row) => {
    setEditingInstanceId(row.instance_id || "");
    setEditAssignedUsersArr(parseAssignedUsers(row.assigned_users));
  };

  const cancelEdit = () => {
    setEditingInstanceId("");
    setEditAssignedUsersArr([]);
  };

  const saveEdit = async (row) => {
    if (!row?.instance_id) {
      setStatusError("Instance ID is required to update assigned users.");
      return;
    }

    try {
      setStatusError("");
      setSavingEdit(true); 
      setStatusLoading(true);

      const assignedUsers = normalizeAssignedUsers(editAssignedUsersArr);

      await updateAssignedUsersAPI({
        instanceId: row.instance_id,
        assignedUsers,
        // later: you can also send moveWarningsByUser if backend needs it
      });

      // close edit UI + clear warnings
      setEditingInstanceId("");
      setEditAssignedUsersArr([]);
      setMoveWarningsByUser({});

      // refresh EVERYTHING: all VM cards + unassigned users
      await handleRefreshStatus();

    } catch (err) {
      setStatusError(
        err?.response?.data?.message || err?.message || "Failed to update assigned users."
      );
    } finally {
      setSavingEdit(false); 
      setStatusLoading(false);
    }
  };

  // -------------------- list vms --------------------
  const handleRefreshStatus = async () => {
    setStatusError("");

    try {
      setStatusLoading(true);

      const resp = await listUserVmsAPI();
      const items = Array.isArray(resp?.items) ? resp.items : [];
      const vm_meta = resp?.vm_meta && typeof resp.vm_meta === "object" ? resp.vm_meta : {};
      const states = resp?.states && typeof resp.states === "object" ? resp.states : {};

      setAllUsers(items);
      setVmMetaById(vm_meta);
      setStatesById(states);

      // map instance_id -> [usernames]
      const map = new Map();
      const unassigned = [];

      for (const u of items) {
        const uname = (u?.username || "").trim();
        const iid = (u?.assigned_vm || "").trim();
        if (!uname) continue;

        if (!iid) {
          unassigned.push(uname);
          continue;
        }

        if (!map.has(iid)) map.set(iid, []);
        map.get(iid).push(uname);
      }

      const allVmIds = Array.from(
        new Set([...Object.keys(vm_meta || {}), ...Array.from(map.keys())])
      );

      // IMPORTANT: merge backend truth + action overlay (starting/stopping)
      const rows = allVmIds.map((instance_id) => {
        const meta = vm_meta?.[instance_id] || {};
        const backend = String(states?.[instance_id] || "unknown").toLowerCase();

        let state = backend;

        const overlay = actionById?.[instance_id]; // "starting" | "stopping" | undefined
        if (overlay === "starting" && backend !== "running") state = "starting";
        if (overlay === "stopping" && backend !== "stopped") state = "stopping";

        const users = (map.get(instance_id) || []).slice().sort((a, b) => a.localeCompare(b));

        return {
          instance_id,
          instance_name: meta.instance_name || "—",
          region: meta.region || "—",
          launch_time: meta.launch_time || "",
          state,
          assigned_users: users.join(","),
        };
      });

      rows.sort((a, b) => String(a.instance_id).localeCompare(String(b.instance_id)));
      unassigned.sort((a, b) => a.localeCompare(b));

      setVmRows(rows);
      setUnassignedUsers(unassigned);

      // Clear overlays only when backend reaches stable target
      setActionById((prev) => {
        const next = { ...(prev || {}) };
        for (const [iid, overlay] of Object.entries(next)) {
          const backend = String(states?.[iid] || "").toLowerCase();
          if (overlay === "starting" && backend === "running") delete next[iid];
          if (overlay === "stopping" && backend === "stopped") delete next[iid];
        }
        return next;
      });

      if (!rows.length) setStatusError("No VMs to show.");
    } catch (err) {
      setStatusError(err?.response?.data?.message || err?.message || "Failed to fetch VM records.");
      setAllUsers([]);
      setVmMetaById({});
      setStatesById({});
      setVmRows([]);
      setUnassignedUsers([]);
    } finally {
      setStatusLoading(false);
    }
  };


  // auto-load vm list on page load
  useEffect(() => {
    handleRefreshStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------- submit: create vm --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });
    setAcceptCostsErr("");

    if (!instanceName) {
      setStatus({ type: "danger", msg: "Instance name is required." });
      return;
    }

    const costErr = validateAcceptCosts(acceptCosts);
    if (costErr) {
      setAcceptCostsErr(costErr);
      return;
    }

    // Validate BEFORE entering try/submitting
    if (trim(targetOrg) && !trim(targetUser)) {
      setStatus({
        type: "danger",
        msg: "Please select a user when an organization is chosen.",
      });
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        action: "launchUserVm",
        requestedBy: username,
        instanceName: instanceName.trim(),
      };

      if (trim(targetOrg)) {
        payload.organization = trim(targetOrg).toLowerCase();
      }

      const resp = await startUserVmAPI(payload);

      const newInstanceId = resp?.instanceId;

      // ✅ Only assign if org + user are present
      if (newInstanceId && trim(targetOrg) && trim(targetUser)) {
        await updateAssignedUsersAPI({
          instanceId: newInstanceId,
          assignedUsers: trim(targetUser).toLowerCase(),
        });
      }

      setStatus({
        type: "success",
        msg:
          trim(targetOrg) && trim(targetUser)
            ? `VM ${newInstanceId || ""} creation started and assigned to ${targetUser}. Refresh to see status.`
            : resp?.message || "VM creation started. VM list will refresh shortly.",
      });

      setInstanceName("");
      setTargetOrg("");      // ✅ optional: reset org to Not Assigned after create
      setTargetUser("");
      setUserOptions([]);    // ✅ optional: keep UI clean if org reset
      setAcceptCosts(false);

      await handleRefreshStatus();
    } catch (err) {
      setStatus({
        type: "danger",
        msg: err?.response?.data?.message || err?.message || "Failed to create VM.",
      });
    } finally {
      setSubmitting(false);
    }
  };


  const allUsernames = React.useMemo(() => {
    return Array.from(
      new Set((allUsers || []).map((u) => (u?.username || "").trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [allUsers]);

  // -------------------- start/stop --------------------
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const handleStartVm = async (row) => {
    const iid = row?.instance_id;
    if (!iid) return;

    try {
      setStatusError("");

      // overlay immediately
      setActionById((p) => ({ ...(p || {}), [iid]: "starting" }));

      // optimistic row update so user sees it instantly
      setVmRows((prev) => prev.map((r) => (r.instance_id === iid ? { ...r, state: "starting" } : r)));

      await startUserVmInstanceAPI(iid);

      // poll until running (or timeout)
      const MAX_WAIT_MS = 90000;
      const POLL_MS = 4000;
      const start = Date.now();

      while (Date.now() - start < MAX_WAIT_MS) {
        await sleep(POLL_MS);
        const r = await listUserVmsAPI();
        const s = String(r?.states?.[iid] || "").toLowerCase();
        await handleRefreshStatus();
        if (s === "running") break;
      }
    } catch (err) {
      setStatusError(err?.response?.data?.message || err?.message || "Failed to start VM instance.");
      // clear overlay on error
      setActionById((p) => {
        const next = { ...(p || {}) };
        delete next[iid];
        return next;
      });
      await handleRefreshStatus();
    }
  };

  const handleStopVm = async (row) => {
    const iid = row?.instance_id;
    if (!iid) return;

    try {
      setStatusError("");

      // overlay immediately
      setActionById((p) => ({ ...(p || {}), [iid]: "stopping" }));

      // optimistic row update so user sees it instantly
      setVmRows((prev) => prev.map((r) => (r.instance_id === iid ? { ...r, state: "stopping" } : r)));

      await stopUserVmInstanceAPI(iid);

      // poll until stopped (or timeout)
      const MAX_WAIT_MS = 90000;
      const POLL_MS = 4000;
      const start = Date.now();

      while (Date.now() - start < MAX_WAIT_MS) {
        await sleep(POLL_MS);
        const r = await listUserVmsAPI();
        const s = String(r?.states?.[iid] || "").toLowerCase();
        await handleRefreshStatus();
        if (s === "stopped") break;
      }
    } catch (err) {
      setStatusError(err?.response?.data?.message || err?.message || "Failed to stop VM instance.");
      // clear overlay on error
      setActionById((p) => {
        const next = { ...(p || {}) };
        delete next[iid];
        return next;
      });
      await handleRefreshStatus();
    }
  };


  const handleDeleteVm = async (row) => {
    if (!row?.instance_id) {
      setStatusError("Instance ID is missing for this VM row.");
      return;
    }

    const rawState = row.state ?? "unknown";
    const state = typeof rawState === "string" ? rawState.toLowerCase() : "unknown";
    const hasUsers = parseAssignedUsers(row.assigned_users).length > 0;

    // Safety guard (UI already disables, but keep backend-safe behavior in UI too)
    if (state === "running") {
      setStatusError("VM must be stopped before it can be deleted.");
      return;
    }
    if (hasUsers) {
      setStatusError("Remove all assigned users before deleting this VM.");
      return;
    }

    const ok = window.confirm(
      `Delete VM "${row.instance_name || row.instance_id}"?\n\nThis action will remove the VM record (dummy for now).`
    );
    if (!ok) return;

    try {
      setStatusError("");
      setDeletingInstanceId(row.instance_id);
      setStatusLoading(true);

      const resp = await deleteUserVmAPI(row.instance_id);

      setStatus({
        type: "success",
        msg: resp?.message || "Delete VM requested (dummy).",
      });

      // refresh list
      await handleRefreshStatus();
    } catch (err) {
      setStatusError(err?.response?.data?.message || err?.message || "Failed to delete VM.");
    } finally {
      setDeletingInstanceId("");
      setStatusLoading(false);
    }
  };


  return (
    <div className="page-wrapper overflow-hidden">
      {/* ---------- CREATE VM CARD ---------- */}
      <Row className="justify-content-center muc-container">
        <Col sm="12" md="12" lg="12">
          <ComponentCard>
            <h4 className="muc-title">Create New User VM</h4>

            {status.msg && (
              <Alert color={status.type} isOpen={!!status.msg} toggle={() => setStatus({ type: "", msg: "" })}>
                {status.msg}
              </Alert>
            )}

            <div style={{ display: "flex", justifyContent: "center" }}>
              <Card className="muc-card" style={{ maxWidth: "600px", width: "100%" }}>
                <CardBody className="muc-card-body">
                  <Form onSubmit={handleSubmit}>
                    {/* Organization */}
                    <div className="muc-field-row">
                      <div className="muc-label-cell">Organization:</div>
                      <div>
                        <Input
                          type="select"
                          className="muc-input"
                          value={targetOrg}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTargetOrg(v);
                            setTargetUser("");
                            if (!v) {
                              setUserOptions([]);
                            }
                          }}
                        >
                          <option value="">
                            {orgLoading ? "Loading organizations..." : "— Not assigned (optional) —"}
                          </option>

                          {orgOptions.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </Input>

                      </div>
                    </div>

                    {/* Username — required when org selected */}
                    {trim(targetOrg) && (
                      <div className="muc-field-row">
                        <div className="muc-label-cell">Assign to User:</div>
                        <div>
                          <Input
                            type="select"
                            className="muc-input"
                            value={targetUser}
                            onChange={(e) => setTargetUser(e.target.value)}
                            required
                          >
                            <option value="" disabled>
                              {usersLoading ? "Loading users..." : "Select user"}
                            </option>

                            {userOptions.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </Input>
                        </div>
                      </div>
                    )}

                    {/* Instance Name */}
                    <div className="muc-field-row">
                      <div className="muc-label-cell">Instance Name:</div>
                      <div>
                        <Input
                          className="muc-input"
                          value={instanceName}
                          onChange={(e) => setInstanceName(e.target.value)}
                          placeholder="e.g. training-gpu-01"
                          required
                        />
                      </div>
                    </div>

                    {/* Credits / Cost acknowledgement */}
                    <div className="muc-field-row">
                      <div className="muc-label-cell">Credits / Cost:</div>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Input
                          type="checkbox"
                          checked={acceptCosts}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setAcceptCosts(checked);
                            if (acceptCostsErr) setAcceptCostsErr(validateAcceptCosts(checked));
                          }}
                          onBlur={() => setAcceptCostsErr(validateAcceptCosts(acceptCosts))}
                          style={{ width: "20px", height: "18px" }}
                        />
                        <span style={{ marginLeft: 10, fontSize: "12px", color: "#666" }}>
                          I understand this will start a cloud VM and consume credits / incur cloud charges while it is running.
                        </span>
                      </div>
                      {acceptCostsErr && (
                        <div style={{ color: "#dc3545", fontSize: "12px", marginTop: 4 }}>{acceptCostsErr}</div>
                      )}
                    </div>

                    <div className="muc-submit-bar">
                      <Button className="submit-action-button muc-submit-btn" disabled={submitting || !formValid} type="submit">
                        {submitting ? (
                          <>
                            <Spinner size="sm" style={{ marginRight: 8 }} />
                            Creating...
                          </>
                        ) : (
                          "Create VM"
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </div>
          </ComponentCard>
        </Col>
      </Row>

      {/* ---------- USER VM MAPPINGS (cards) ---------- */}
      <Row className="justify-content-center muc-container">
        <Col sm="12" md="12" lg="12" style={{ marginTop: 10 }}>
          <ComponentCard>
            <h4 className="muc-title">User VM Mappings</h4>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -36 }}>
              <Button
                className="submit-action-button muc-submit-btn"
                type="button"
                disabled={statusLoading}
                onClick={handleRefreshStatus}
                title="Refresh VM mappings"
                style={{ padding: "6px 10px", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                {statusLoading ? <Spinner size="sm" /> : <RefreshIcon fontSize="small" />}
              </Button>
            </div>

            <label style={{ fontSize: "15px", marginBottom: "10px", display: "block" }}>
              <div
                style={{
                  marginLeft: "100px",
                  marginRight: "100px",
                  fontSize: "14px",
                  marginBottom: "10px",
                  textAlign: "left",
                  marginTop: "10px",
                  color: "#333",
                }}
              >
                <ul style={{ paddingLeft: "18px", marginBottom: 0 }}>
                  <li>
                    This section shows all users and VMs and their assignment mappings.
                  </li>

                  <li>
                    Click the <span style={{ fontWeight: 800 }}>Refresh</span> button to load the latest VM status and assignments.
                  </li>

                  <li>
                    <span style={{ fontWeight: 800 }}>Start</span> (<span style={{ color: "green" }}>▶</span>) is enabled only when VM is <code>stopped</code>.
                    <br />
                    <span style={{ fontWeight: 800 }}>Stop</span> (<span style={{ color: "red" }}>■</span>) is enabled only when VM is <code>running</code>.
                  </li>

                  <li>
                    Click the <span style={{ fontWeight: 800 }}>Edit</span> icon to update assigned users:
                    <ul style={{ paddingLeft: "18px" }}>
                      <li>Select/Deselect users via checkboxes and click <span style={{ fontWeight: 800 }}>Save</span> to add/remove users to existing VMs.</li>
                      <li>
                        If a user is already assigned to another VM, you will see a warning and confirmation.
                        Confirming will move the user to the current VM.
                      </li>
                      <li>
                        To move a user from one VM to another, directly edit the target VM and add the user there by confirming the reassignment alert/warning. No need to explicitly remove the user from the existing machine and then adding to another.
                      </li>
                      <li>
                        Users with no VM assignment appear in <span style={{ fontWeight: 800 }}>Unassigned Users</span>.
                      </li>
                    </ul>
                  </li>

                  <li>
                    <span style={{ fontWeight: 800 }}>Note:</span> Running VMs consume credits / cloud cost. Stop VMs when not needed.
                  </li>
                </ul>
              </div>
            </label>

            {statusError && (
              <Alert color="danger" isOpen={!!statusError} toggle={() => setStatusError("")} style={{ marginTop: 12 }}>
                {statusError}
              </Alert>
            )}

            <Card style={{ marginTop: 35, borderRadius: 12, color: "#333", padding: 12, background: "#fafafa" }}>
              <h6 style={{ marginBottom: 10, fontWeight: 800 }}>Unassigned Users</h6>

              {unassignedUsers.length === 0 ? (
                <div style={{ textAlign: "center", color: "#777", fontSize: 12, padding: "8px 0" }}>
                  No unassigned users.
                </div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 4 }}>
                  {unassignedUsers.map((u) => (
                    <span
                      key={u}
                      style={{
                        padding: "4px 10px",
                        border: "1px solid #eee",
                        borderRadius: 999,
                        fontSize: 12,
                      }}
                    >
                      {u}
                    </span>
                  ))}
                </div>
              )}
            </Card>


            <div style={{ marginTop: 14 }}>
              {statusLoading && vmRows.length === 0 ? (
                <div style={{ textAlign: "center", color: "#777" }}>Loading...</div>
              ) : vmRows.length === 0 ? (
                <div style={{ textAlign: "center", color: "#777" }}>No VMs to show.</div>
              ) : (
                <Row className="g-3">
                  {vmRows.map((r, idx) => {
                    const rawState = r.state ?? "unknown";
                    const state = typeof rawState === "string" ? rawState.toLowerCase() : "unknown";

                    const isStable = state === "running" || state === "stopped";
                    const isTransition = !isStable; // includes starting/stopping/pending/etc

                    const canStart = state === "stopped";
                    const canStop = state === "running";
                    const hasUsers = parseAssignedUsers(r.assigned_users).length > 0;
                    const isEditing = editingInstanceId === r.instance_id;

                    const canDelete = isStable && state !== "running" && !hasUsers && !isEditing;
                    const deleteDisabledReason =
                      isEditing
                        ? "Exit edit mode to delete."
                        : state === "running"
                        ? "Stop the VM before deleting."
                        : hasUsers
                        ? "Remove all assigned users before deleting."
                        : "Delete this VM.";
                    
                    const deleteTooltip =
                         deletingInstanceId === r.instance_id ? "Deleting..." : deleteDisabledReason;

                    return (
                      <Col key={r.instance_id || idx} xs="12" md="6" lg="4">
                        <Card style={{ borderRadius: 12, height: "100%" }}>
                          <CardBody>
                            {/* Header */}
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                              <div style={{ minWidth: 0 }}>
                                <h5 style={{ marginBottom: 20, wordBreak: "break-word", fontWeight: 600 }}>
                                  {r.instance_name || "—"}
                                </h5>
                                <div style={{ fontSize: 12, color: "#666", wordBreak: "break-word" }}>
                                  Instance ID: <span style={{ color: "#111" }}>{r.instance_id || "—"}</span>
                                </div>

                                <div style={{ fontSize: 12, color: "#666" }}>
                                  Region: <span style={{ color: "#111" }}>{r.region || "—"}</span>
                                </div>
                                <div style={{ fontSize: 12, color: "#666" }}>
                                  Status:{" "}
                                  <span style={{ color: "#111", textTransform: "capitalize" }}>
                                    {state}

                                    {(state === "starting" || state === "stopping") && (
                                      <Spinner size="sm" style={{ marginLeft: 8 }} />
                                    )}
                                  </span>
                                </div>

                              </div>

                              {/* actions */}
                              <div style={{ display: "flex", gap: 10, alignItems: "start" }}>
                                {canStart && (
                                  <Button
                                    color="link"
                                    size="sm"
                                    disabled={Boolean(statusLoading || isTransition)}
                                    onClick={() => handleStartVm(r)}
                                    title="Start VM"
                                    style={{ padding: 0 }}
                                  >
                                    <PlayArrowIcon fontSize="small" style={{ color: "green" }} />
                                  </Button>
                                )}

                                {canStop && (
                                  <Button
                                    color="link"
                                    size="sm"
                                    disabled={Boolean(statusLoading || isTransition || !r.instance_id)}
                                    onClick={() => handleStopVm(r)}
                                    title="Stop VM"
                                    style={{ padding: 0 }}
                                  >
                                    <StopIcon fontSize="small" style={{ color: "red" }} />
                                  </Button>
                                )}

                                <span title={deleteTooltip} 
                                      style={{
                                        display: "inline-flex",
                                        cursor: canDelete ? "pointer" : "not-allowed",
                                      }}>
                                  <Button
                                    color="link"
                                    size="sm"
                                    disabled={Boolean(
                                      statusLoading ||
                                      isTransition ||
                                      deletingInstanceId === r.instance_id ||
                                      !canDelete
                                    )}
                                    onClick={() => handleDeleteVm(r)}
                                    style={{ padding: 0 }}
                                  >
                                    {deletingInstanceId === r.instance_id ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <DeleteIcon
                                        fontSize="small"
                                        style={{ color: canDelete ? "#dc3545" : "#aaa" }}
                                      />
                                    )}
                                  </Button>
                                </span>


                                {!isEditing ? (
                                  <Button
                                    color="link"
                                    size="sm"
                                    disabled={Boolean(statusLoading || isTransition || !r.instance_id)}
                                    onClick={() => startEdit(r)}
                                    title="Edit assigned users"
                                    style={{ padding: 0 }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </Button>
                                ) : (
                                  <>
                                    <Button
                                      color="link"
                                      size="sm"
                                      disabled={statusLoading || savingEdit}
                                      onClick={() => saveEdit(r)}
                                      title={savingEdit ? "Saving..." : "Save"}
                                      style={{ padding: 0 }}
                                    >
                                      {savingEdit ? <Spinner size="sm" /> : <SaveIcon fontSize="small" style={{ color: "green" }} />}
                                    </Button>

                                    <Button
                                      color="link"
                                      size="sm"
                                      disabled={statusLoading || savingEdit}
                                      onClick={cancelEdit}
                                      title="Cancel"
                                      style={{ padding: 0 }}
                                    >
                                      <CloseIcon fontSize="small" style={{ color: "#666" }} />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>

                            <hr style={{ margin: "12px 0" }} />

                            {/* Assigned users display */}
                            {!isEditing ? (
                              <div style={{ fontSize: 13 }}>
                                <strong>Assigned Users:</strong>
                                <div style={{ marginTop: 6, color: "#111", wordBreak: "break-word" }}>
                                  {r.assigned_users ? r.assigned_users : <span style={{ color: "#777" }}>—</span>}
                                </div>
                              </div>
                            ) : (
                              <div style={{ marginTop: 8 }}>
                                <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                                  Toggle users for this VM (checkboxes)
                                </div>

                                <div
                                  style={{
                                    border: "1px solid #e9e9e9",
                                    borderRadius: 10,
                                    padding: 10,
                                    maxHeight: 220,
                                    overflowY: "auto",
                                    background: "#fff",
                                  }}
                                >
                                  {allUsernames.length === 0 ? (
                                    <div style={{ color: "#777", fontSize: 12 }}>No users available.</div>
                                  ) : (
                                    allUsernames.map((u) => {
                                      const currentAssigned = (assignedVmByUser[u] || "").trim();
                                      const assignedElsewhere =
                                        currentAssigned && currentAssigned !== editingInstanceId;
                                      const assignedVmName =
                                        vmMetaById?.[currentAssigned]?.instance_name || currentAssigned;
                                      const checked = isChecked(u);
                                      const moveWarn = moveWarningsByUser[(u || "").toLowerCase()];

                                      return (
                                        <label
                                          style={{
                                            cursor: savingEdit ? "wait" : "pointer",
                                            opacity: savingEdit ? 0.6 : 1,
                                            userSelect: "none",
                                            borderBottom: "1px solid #f3f3f3",
                                            padding: "6px 6px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 10,
                                          }}
                                        >

                                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <Input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={() => onToggleUserWithWarning(u, currentAssigned)}
                                              style={{ margin: 0 }}
                                              disabled={savingEdit} 
                                            />
                                            <span style={{ fontSize: 13, color: "#111" }}>{u}</span>
                                          </div>

                                          {assignedElsewhere ? (
                                            <span
                                              style={{
                                                fontSize: 11,
                                                padding: "2px 8px",
                                                borderRadius: 999,
                                                border: "1px solid #eee",
                                                background: "#fafafa",
                                                color: "#666",
                                                whiteSpace: "nowrap",
                                              }}
                                              title={`Currently assigned to ${currentAssigned}`}
                                            >
                                              Assigned: {assignedVmName}
                                            </span>
                                          ) : null}

                                          {/* If user is selected and we warned → show "will move" */}
                                          {checked && moveWarn ? (
                                            <span
                                              style={{
                                                fontSize: 11,
                                                padding: "2px 8px",
                                                borderRadius: 999,
                                                border: "1px solid #f5c2c7",
                                                background: "#f8d7da",
                                                color: "#842029",
                                                whiteSpace: "nowrap",
                                              }}
                                              title={`Will move from ${moveWarn.from}`}
                                            >
                                              Will move from: {moveWarn.fromLabel}
                                            </span>
                                          ) : null}

                                        </label>
                                      );
                                    })
                                  )}
                                </div>

                                {Object.keys(moveWarningsByUser).length > 0 && (
                                  <Alert color="warning" style={{ marginTop: 10, fontSize: 12 }}>
                                    Some users you selected are currently assigned to a different VM and will be moved here.
                                  </Alert>
                                )}


                                <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
                                  Checked users will be assigned to this VM.
                                </div>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </div>
          </ComponentCard>
        </Col>
      </Row>
    </div>
  );
}
